// ReverseEntry — THE ONE DOOR FOR UNDOING A MONEY ENTRY.
//
// Operator, 17 Sept 2026, on Money movements showing RCPT-2026-09-16-JAPCJ
// (Money in, WK May, Hot wallet · TRC, +25,80,966.58 USDT): "How to void this
// entry? no option, need options in party pages & money/trade screens".
//
// A trade has had Void in its panel since 22 Aug (DealSlideOver). A money
// entry — a receipt, a payment, a move between accounts, an adjustment, a
// cost, the owner's money — could only be reversed by hand with
// scripts/revert-tx.sh. This is that door on screen, written ONCE: Money
// movements seats it on every live money row, the party statement on every
// live money entry ("fix once, applies everywhere"). Trade rows keep opening
// the deal panel, where Void lives; the route refuses a trade posting so the
// two doors can never cross.
//
// WORDS. A money entry is REVERSED (the statement already says so); a trade is
// VOIDED. Nothing here shows a slug or a ledger id — the reference and the
// names only; the transaction id travels in the call.
//
// THE SHAPE. One small "Reverse this entry" button on the row; the panel
// opens beneath it: the reason chips (the trade void's words where they fit)
// and a free line, a read-back sentence naming the reference, the amount, the
// party and the account, and one red button. A reversal happens only on that
// button, with a reason. The route's answers, in words:
//   drawn_by_deal   a settled trade drew on this money — refused; the way out
//                   is Settle → Reopen, and the panel links there
//   confirm         an awaiting trade of the same party could still settle
//                   from it — a warning naming the trade, and "Reverse anyway"
// On success every money view refreshes (refreshMoneyViews), so the row leaves
// the live list at once — which is why the success line is not inside the
// row: the screen seats `ReversedLine` above its list, and it says "Reversed.
// The entry stays under Show reversed." with a "Book it again →" link that
// carries the figures into the right entry screen (Record money, Move money,
// Adjust), prefilled from the entry.
//
// TWO WAYS TO SEAT IT. `ReverseEntry` is the button and the panel together —
// for a card, where the panel simply follows. A TABLE row cannot hold a panel
// inside one cell, so it seats `ReverseEntryButton` in the row and
// `ReverseEntryPanel` in a full-width row beneath it, holding `open` itself.
// Both are this one file; the logic lives in the panel.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, refreshMoneyViews, type DrawableDeal, type MovementState, type ReverseMovementResponse } from '../lib/api';
import { ApiError, errorWords } from '../lib/errors';
import { ErrorLine, WarnLine } from './ErrorLine';
import { displayAsset, fmtAmount, fmtDate, isoToDubaiWall } from '../lib/format';
import { partyName } from '../lib/naming';

/** What the door needs to know about an entry. Money movements hands its row
 *  through as it is; the statement builds one from its entry (no account on
 *  a statement row — the "Book it again" link then leaves the account for
 *  the operator to pick). */
export interface ReversibleEntry {
  /** The ledger transaction id — travels in the call, never on screen. */
  txn_id: string;
  reference: string | null;
  /** Money movements' kind (`money_in`, `rail_move`, …) or the statement's
   *  raw ledger type (`transfer`, `rebalance`, …). Decides only which entry
   *  screen "Book it again" opens, and keeps a trade out. */
  kind: string;
  /** The BFF's own sentence — the read-back uses it, so both screens say
   *  the same thing about the same entry. */
  words: string;
  at?: string | null;
  state: MovementState;
  /** A trade or a cheque leg names its deal; such an entry has no door. */
  deal_id?: string | null;
  party?: { slug: string; display_name: string; entity?: string } | null;
  rail?: { slug: string; label: string } | null;
  /** Where a move between accounts landed. */
  counter_rail?: { slug: string; label: string } | null;
  asset?: string | null;
  /** Display units, positive; `direction` carries the sign. On a payment
   *  with a cost on top this is what LEFT THE ACCOUNT (party + cost). */
  amount?: number | null;
  direction?: 'in' | 'out' | 'move' | null;
  /** The cost carried inside the movement (a fee on top of a payment): what
   *  the desk paid beyond the party's figure, and its category. */
  cost?: { amount: number; category: string } | null;
}

/** Which entries get the door: live, and not a trade's posting. The route
 *  refuses the rest in words; this keeps the button off them in the first
 *  place. */
export function canReverse(e: Pick<ReversibleEntry, 'state' | 'kind' | 'deal_id'>): boolean {
  if (e.state !== 'live') return false;
  if (e.deal_id) return false;
  const k = e.kind.toLowerCase();
  return k !== 'trade' && k !== 'trade_unsettled' && k !== 'trade_margin' && !k.startsWith('mc_');
}

/** WHY AN ENTRY IS REVERSED, AS CHIPS — the trade void's words where they
 *  fit (DealSlideOver VOID_REASONS), plus the two a money entry needs. A chip
 *  alone is a reason; the free line adds to it. */
export const REVERSE_REASONS = ['Wrong amount', 'Wrong party', 'Wrong account', 'Booked twice', 'Never happened'] as const;

/** What the screen seats above its list once an entry is reversed. */
export interface ReversedOutcome {
  reference: string | null;
  /** The entry's own sentence, for the line. */
  words: string;
  /** The right entry screen, prefilled; null when no screen books this kind. */
  again: { href: string; label: string } | null;
  chain_untied: number;
  manual_ties_left: number;
}

/** WHERE TO BOOK IT AGAIN. A receipt or payment → Record money with the party,
 *  the amount, the asset, the account and the time; a move → Move money with
 *  both accounts, the amount and the time; an adjustment → Adjust with the
 *  account (or the party). Only the parameters those screens honour. Other
 *  kinds — a cost, the owner's money, between parties — have no link. */
export function bookAgainLink(e: ReversibleEntry): { href: string; label: string } | null {
  const k = e.kind.toLowerCase();
  const when = e.at ? isoToDubaiWall(e.at) : '';
  const asset = e.asset ?? '';
  // THE PARTY'S FIGURE, NOT THE ACCOUNT'S (S7, 21 Sept 2026). Since the fee
  // went on top (17 Sept) a payment's movement amount is party + cost — what
  // left the account. Record money asks for what the party gets and the fee
  // beside it, so the link carries the two apart; prefilled whole, the party
  // would have been paid the fee a second time.
  const costOnTop = e.direction === 'out' && e.cost && e.cost.amount > 0 ? e.cost : null;
  const partyFigure = e.amount != null && e.amount > 0
    ? (costOnTop ? Number((e.amount - costOnTop.amount).toFixed(8)) : e.amount)
    : 0;
  const amount = partyFigure > 0 ? String(partyFigure) : '';
  const isRecord = k === 'money_in' || k === 'money_out'
    || k === 'transfer' || k === 'receipt' || k === 'payment' || k === 'party_payment_split' || k === 'movement';
  if (isRecord) {
    // Record money always has a party on the other side; an entry without
    // one (an external receipt) has no screen to book it again from.
    if (!e.party || !e.direction || e.direction === 'move') return null;
    const q = new URLSearchParams({ dir: e.direction, party: e.party.slug });
    if (amount) q.set('amount', amount);
    if (costOnTop) { q.set('cost', String(costOnTop.amount)); q.set('costCategory', costOnTop.category); }
    if (asset) q.set('asset', asset);
    if (e.rail && asset) { q.set('rail', e.rail.slug); q.set('railAsset', asset); }
    if (when) q.set('when', when);
    return { href: `/record?${q}`, label: 'Book it again →' };
  }
  if (k === 'rail_move') {
    if (!e.rail || !asset) return null;
    const q = new URLSearchParams({ rail: e.rail.slug, railAsset: asset });
    if (e.counter_rail) { q.set('to', e.counter_rail.slug); q.set('toAsset', asset); }
    if (amount) q.set('amount', amount);
    if (when) q.set('when', when);
    return { href: `/treasury/rebalance?${q}`, label: 'Book it again →' };
  }
  if (k === 'adjustment' || k === 'rebalance' || k === 'correction' || k === 'reclass') {
    if (e.rail && asset) {
      const q = new URLSearchParams({ rail: e.rail.slug, railAsset: asset });
      return { href: `/treasury/adjust?${q}`, label: 'Book it again →' };
    }
    if (e.party) return { href: `/treasury/adjust?${new URLSearchParams({ party: e.party.slug })}`, label: 'Book it again →' };
    return null;
  }
  return null;
}

/** The read-back: the reference, the BFF's sentence, the Dubai time. Reversal
 *  suffixes never apply — only a live entry has the door. */
function readback(e: ReversibleEntry): string {
  const head = e.words.charAt(0).toLowerCase() + e.words.slice(1);
  const when = e.at ? `, ${fmtDate(e.at)}` : '';
  return `Reverses ${e.reference ?? 'this entry'} — ${head}${when}.`;
}

// ── the button ──────────────────────────────────────────────────────────────

/** `short`: one word on the button ("Reverse") where the row gives it a
 *  narrow column of its own; the title keeps the whole phrase. Sized for the
 *  13–13.5px ledger row it sits in (components/Ledger) — never the smallest
 *  text on the line. */
export function ReverseEntryButton({ open, onClick, short = false, plain = false, className = '' }: {
  open: boolean; onClick: () => void; short?: boolean;
  /** A bare link instead of a bordered button — the phone card's second
   *  line has three figures on it and no room for a box (19 Sept 2026). */
  plain?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={plain
        ? `btn-link text-meta whitespace-nowrap max-lg:p-2.5 max-lg:-m-2.5 ${open ? 'opacity-60' : ''} ${className}`
        : `btn-secondary text-meta py-0.5 px-2 whitespace-nowrap ${open ? 'opacity-60' : ''} ${className}`}
      aria-expanded={open}
      title="Reverse this entry"
      onClick={onClick}
    >
      {short ? 'Reverse' : 'Reverse this entry'}
    </button>
  );
}

// ── the panel ───────────────────────────────────────────────────────────────

export function ReverseEntryPanel({ entry, onClose, onReversed, className = '' }: {
  entry: ReversibleEntry;
  onClose: () => void;
  /** The screen seats the outcome above its list — the row itself leaves
   *  the live list on the refresh that follows. */
  onReversed: (o: ReversedOutcome) => void;
  className?: string;
}) {
  const qc = useQueryClient();
  const [chip, setChip] = useState<(typeof REVERSE_REASONS)[number] | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The route's two books-side answers, kept apart from a plain error: each
  // has its own words and its own way out.
  const [drawable, setDrawable] = useState<{ words: string; deals: DrawableDeal[] } | null>(null);
  const [drawn, setDrawn] = useState<string | null>(null);
  // The chip and the free line together are the reason the books keep.
  const reason = [chip, text.trim()].filter(Boolean).join(' — ');
  const reasonOk = reason.length > 0;

  const send = async (confirmDrawable: boolean) => {
    setBusy(true); setError(null);
    try {
      const r: ReverseMovementResponse = await api.reverseMovement(entry.txn_id, {
        reason, ...(confirmDrawable ? { confirm_drawable: true } : {}),
      });
      refreshMoneyViews(qc);
      onReversed({
        reference: r.reference ?? entry.reference,
        words: entry.words,
        again: bookAgainLink(entry),
        chain_untied: r.chain_untied ?? 0,
        manual_ties_left: r.manual_ties_left ?? 0,
      });
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'confirm' && e.body.field === 'confirm_drawable') {
        setDrawable({ words: e.message, deals: (e.body.deals as DrawableDeal[] | undefined) ?? [] });
      } else if (e instanceof ApiError && e.code === 'drawn_by_deal') {
        setDrawn(e.message);
      } else {
        setError(errorWords(e));
      }
    } finally { setBusy(false); }
  };

  const amountWords = entry.amount != null && entry.asset
    ? `${fmtAmount(entry.amount, entry.asset)} ${displayAsset(entry.asset)}` : null;
  const who = entry.party ? partyName(entry.party) : null;

  return (
    <div className={`rounded-lg border border-stroke-soft bg-panel-2/50 px-3 py-2.5 space-y-2 text-meta ${className}`}>
      <p className="font-medium text-body">Reverse this entry?</p>
      <p className="text-tg-hint">{readback(entry)}</p>
      {/* The figures once more, plainly, for the one time the sentence is
          long: what, who, where. */}
      {(amountWords || who || entry.rail) && (
        <p className="text-tg-hint">
          {[amountWords, who, entry.rail?.label].filter(Boolean).join(' · ')}
        </p>
      )}

      {/* THE REASON: a chip, a few words, or both. */}
      <div className="flex flex-wrap gap-1.5">
        {REVERSE_REASONS.map((c) => (
          <button key={c} type="button"
            className={`chip ${chip === c ? 'chip-on' : ''}`}
            onClick={() => setChip(chip === c ? null : c)}>
            {c}
          </button>
        ))}
      </div>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={chip === 'Booked twice' ? 'The reference of the other entry' : 'A few words on why'}
        className="input w-full text-field lg:text-body"
      />

      {/* THE TWO BOOKS-SIDE ANSWERS. A settled trade drew on this money:
          refused, the way out named and linked. An awaiting trade could
          still settle from it: a warning, and one more button. */}
      {drawn && (
        <div className="space-y-1">
          {/* words:ok — the route's refusal is the reason; shown whole */}
          <ErrorLine className="">{drawn}</ErrorLine>
          <Link to="/settle" className="btn-link text-meta block">Open Settle →</Link>
        </div>
      )}
      {drawable && (
        // words:ok — the route's warning names the trade that could still settle from this money; shown whole
        <WarnLine className="">{drawable.words}</WarnLine>
      )}
      {/* words:ok — a refusal's own sentence is the reason; shown whole */}
      <ErrorLine className="">{error}</ErrorLine>

      <div className="flex flex-wrap items-center justify-end gap-2 pt-0.5">
        <button type="button" className="btn-link text-meta" onClick={onClose} disabled={busy}>Cancel</button>
        {!drawn && !drawable && (
          <button
            type="button"
            disabled={!reasonOk || busy}
            className="text-body py-1.5 px-3 rounded-lg bg-neg text-white disabled:opacity-40"
            onClick={() => send(false)}
          >
            {busy ? 'Reversing…' : 'Reverse'}
          </button>
        )}
        {drawable && (
          <button
            type="button"
            disabled={!reasonOk || busy}
            className="text-body py-1.5 px-3 rounded-lg bg-neg text-white disabled:opacity-40"
            onClick={() => send(true)}
          >
            {busy ? 'Reversing…' : 'Reverse anyway'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── button + panel, for a card ──────────────────────────────────────────────

export function ReverseEntry({ entry, onReversed, className = '' }: {
  entry: ReversibleEntry;
  onReversed: (o: ReversedOutcome) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!canReverse(entry)) return null;
  return (
    <div className={className}>
      <ReverseEntryButton open={open} onClick={() => setOpen((v) => !v)} />
      {open && (
        <ReverseEntryPanel entry={entry} onClose={() => setOpen(false)} onReversed={onReversed} className="mt-2" />
      )}
    </div>
  );
}

// ── the success line, seated by the screen ──────────────────────────────────

/** "Reversed RCPT-…. The entry stays under Show reversed. Book it again →".
 *  The screen keeps it until the operator closes it or reverses another. */
export function ReversedLine({ outcome, onDismiss, className = '' }: {
  outcome: ReversedOutcome | null;
  onDismiss: () => void;
  className?: string;
}) {
  if (!outcome) return null;
  // Sweep, 21 Oct 2026: two short lines, not one 22-word one. What is done is green; the hand-made chain tie that still
  // points at the reversed entry is a thing left to do, so it is amber and says what to do — nothing was dropped.
  return (
    <div className={`rounded-lg bg-pos-bg px-3 py-2 text-body flex flex-wrap items-center gap-x-3 gap-y-1 ${className}`}>
      <span className="text-pos">
        Reversed{outcome.reference ? ` ${outcome.reference}` : ''}. Entry stays under Show reversed.
      </span>
      {outcome.again && (
        <Link to={outcome.again.href} className="btn-link text-meta" title={outcome.words}>{outcome.again.label}</Link>
      )}
      <button type="button" className="btn-link text-meta ml-auto" onClick={onDismiss}>Close</button>
      {outcome.manual_ties_left > 0 && (
        <span className="basis-full text-meta text-warn">A hand-made chain tie still points at it — untie it on Chain.</span>
      )}
    </div>
  );
}
